import React, { useState } from "react";
import { Icon, Tooltip } from "@culturehq/components";

import { EventLink, ProfileLink } from "app/links";
import { t, Trans } from "app/utils";

import QuickRSVP from "components/common/QuickRSVP";
import Tag from "components/common/event-card/Tag";
import QuickLinks from "components/common/event-card/QuickLinks";
import Event from "components/events/Event";
import EventTime from "components/common/event-card/EventTime";
import Cap from "components/common/Cap";
import { rsvpResponseTypeLabelFrom } from "constants/rsvpResponseTypes";
import Live from "components/common/event-card/Live";

import FeedItemTitle from "./FeedItemTitle";

const EventMetadatum = ({ name, children }) => (
  <div className="row">
    { name && (
      <div className="name">{name}:</div>
    )}
    <div className={name ? "value" : "value-without-label"}>{children}</div>
  </div>
);

const EventCreated = ({ item, onUserActivityUpdate, onUserActivityRemove }) => {
  const user = item.users[0];
  const event = new Event(item.entity);
  const canRSVP = event.canUserRSVP();
  const isDatePast = event.isPast();
  const cancelled = event.cancelledAt;

  const responseType = event.rsvpForUser && event.rsvpForUser.responseType;
  const responseTypeLabel = rsvpResponseTypeLabelFrom(responseType);

  const [rsvp, setRSVP] = useState(event.rsvpForUser || {});
  const onRefreshCap = rsvpValue => {
    setRSVP(rsvpValue);
  };

  return (
    <div className="FeedItem__preview__event_created">
      <FeedItemTitle item={item} date={item.createdAt}>
        <QuickLinks
          event={event}
          inList
          userActivity={item}
          onUserActivityUpdate={onUserActivityUpdate}
          onUserActivityRemove={onUserActivityRemove}
        />

        <h3 className="FeedItem__title">
          {user && (
            <>
              <ProfileLink user={user} />
              {" "}
              <Trans> created a new event: </Trans>
            </>
          )}
          <EventLink event={event} />
        </h3>
      </FeedItemTitle>

      <div className="row">
        <div className="col-md-6 image">
          <EventLink event={event}>
            <div
              className="image-wrapper"
              style={{ backgroundImage: `url(${event.image.mediumUrl})` }}
            >
              <div className="tags-container">
                {event.sponsored && (
                  <Tag type="sponsored">
                    <svg height="22px" viewBox="0 0 440.6 440" width="22px" xmlns="http://www.w3.org/2000/svg">
                      <path d="m220.300781 89.199219c-72.5 0-131.402343 59-131.402343 131.402343 0 72.398438 59 131.398438 131.402343 131.398438 72.398438 0 131.398438-59 131.398438-131.398438 0-72.402343-58.898438-131.402343-131.398438-131.402343zm65.898438 99.902343-79.300781 74.796876c-1.464844 1.4375-3.445313 2.230468-5.5 2.203124-2.148438.019532-4.210938-.847656-5.699219-2.402343l-41.5-42.597657c-3.039063-3.15625-2.972657-8.175781.15625-11.246093 3.125-3.070313 8.140625-3.050781 11.246093.042969l36 37 73.597657-69.398438c3.203125-3.039062 8.261719-2.902344 11.300781.300781s2.902344 8.261719-.300781 11.300781zm0 0" />
                      <path d="m409 268.5 31.601562-47.898438-31.601562-47.902343c-.925781-1.421875-1.382812-3.101563-1.300781-4.800781l3.199219-57.398438-51.097657-25.898438c-1.511719-.761718-2.738281-1.988281-3.5-3.5l-25.800781-51.101562-57.398438 3.199219c-1.699218.082031-3.378906-.371094-4.800781-1.300781l-48-31.597657-47.902343 31.597657c-1.421876.929687-3.101563 1.382812-4.796876 1.300781l-57.402343-3.199219-25.898438 51.101562c-.761719 1.511719-1.988281 2.738282-3.5 3.5l-51.101562 25.796876 3.199219 57.402343c.082031 1.695313-.371094 3.375-1.296876 4.800781l-31.601562 48 31.601562 47.898438c.925782 1.425781 1.378907 3.101562 1.296876 4.800781l-3.199219 57.398438 51.101562 25.800781c1.511719.761719 2.738281 1.988281 3.5 3.5l25.800781 51.101562 57.398438-3.203124h.398438c1.558593.019531 3.085937.46875 4.402343 1.300781l47.898438 31.601562 47.902343-31.601562c1.421876-.925781 3.101563-1.378907 4.796876-1.300781l57.402343 3.203124 25.800781-51.101562c.761719-1.511719 1.988282-2.738281 3.5-3.5l51.097657-25.800781-3.199219-57.398438c.085938-1.703125.601562-3.355469 1.5-4.800781zm-188.699219 99.5c-81.300781 0-147.402343-66.101562-147.402343-147.398438 0-81.300781 66.101562-147.402343 147.402343-147.402343s147.398438 66.101562 147.398438 147.402343c0 81.296876-66.097657 147.398438-147.398438 147.398438zm0 0" />
                    </svg>
                    <span><Trans>Sponsored</Trans></span>
                  </Tag>
                )}
                {!event.public && (
                  <Tag type="private">
                    <Tooltip tip={t("This event is private. Only guests who are invited will see this.")} bottom>
                      <Icon icon="locked" />
                      <span><Trans>Private</Trans></span>
                    </Tooltip>
                  </Tag>
                )}
              </div>
              <Live event={event} />

              <div className="title-block">
                <p className="title">{event.name}</p>
              </div>
            </div>
          </EventLink>
        </div>
        <div className="col-md-6 details">

          {!cancelled && (
            <div className="ecard_detail">
              <Icon icon="clock" />
              <div className="detail_wrapper">
                <EventTime event={event} />
              </div>
            </div>
          )}

          {cancelled && (
            <div className="canceled">
              <EventMetadatum name={t("Status")}>
                <p><Trans>Cancelled</Trans></p>
              </EventMetadatum>
            </div>
          )}

          {event.location && (
            <p className="ecard_detail">
              <Icon icon="loc-ol" /> {event.location}
            </p>
          )}

          <p className="ecard_detail">
            <Icon icon="user" /> <ProfileLink user={event.host} className="host" />
          </p>

          {!isDatePast && (event.cap !== undefined && event.cap !== null) && (
            <p className="ecard_detail">
              <Icon icon="ios-list-outline" /> <Cap event={event} rsvp={rsvp} />
            </p>
          )}

          {isDatePast && responseTypeLabel && (
            <p className="ecard_detail">
              <Icon icon="envelope" /> {responseTypeLabel}
            </p>
          )}

          {!cancelled && !isDatePast && (
            <div className={canRSVP ? "ecard_detail ecard-buttons_container" : "ecard_detail"}>
              <Icon icon="envelope" />
              <EventMetadatum>
                {canRSVP && (
                  <div className="rsvp-container">
                    <QuickRSVP event={event} onRefreshCap={onRefreshCap} background="white" />
                  </div>
                )}
                {!canRSVP && event.inviteOnly && !event.isPast() && (
                  <div className="rsvp-container">
                    <span>Invite Only</span>
                  </div>
                )}
              </EventMetadatum>
            </div>
          )}
          <EventLink className="btn button" event={event}>
            <Trans>More Info</Trans>
          </EventLink>
        </div>
      </div>
    </div>
  );
};

export default EventCreated;
